/**
 * ContractViewModal - Модальное окно просмотра договора
 */
import React, { useState, useEffect } from 'react';
import {
  Modal,
  Text,
  Group,
  Stack,
  Badge,
  Divider,
  Box,
  Button,
  Table,
  ActionIcon,
  Tooltip,
  Loader,
  Center,
} from '@mantine/core';
import {
  IconFileText,
  IconPencil,
  IconDownload,
  IconCalendar,
  IconHash,
  IconX,
  IconUpload,
  IconHistory,
} from '@tabler/icons-react';
import tokens from '../../../theme/designTokens';

// Цвета статусов
const STATUS_COLORS = {
  draft: 'gray',
  pending_signature: 'yellow',
  signed: 'blue',
  active: 'green',
  paused: 'orange',
  terminated: 'red',
};

/**
 * API функции для файлов договора
 */
const contractFilesApi = {
  async getFiles(organizationId, contractId) {
    const response = await fetch(
      `/api/organizations/${organizationId}/contracts/${contractId}/files`,
      { credentials: 'include' }
    );
    if (!response.ok) {
      throw new Error('Не удалось загрузить файлы');
    }
    return response.json();
  },
};

/**
 * Компонент для отображения информационного поля
 */
const InfoField = ({ icon: Icon, label, value }) => {
  if (!value) return null;

  return (
    <Group gap="sm" align="flex-start">
      <Box
        style={{
          width: 32,
          height: 32,
          borderRadius: '8px',
          backgroundColor: tokens.colors.neutral[2],
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Icon size={16} color={tokens.colors.neutral[7]} />
      </Box>
      <Box style={{ flex: 1 }}>
        <Text size="xs" c="dimmed">
          {label}
        </Text>
        <Text size="sm">{value}</Text>
      </Box>
    </Group>
  );
};

const ContractViewModal = ({
  opened,
  onClose,
  contract,
  organizationId,
  onEdit,
  onUploadFile,
}) => {
  const [files, setFiles] = useState([]);
  const [filesLoading, setFilesLoading] = useState(false);
  const [showAllVersions, setShowAllVersions] = useState(false);

  // Загрузка файлов при открытии
  useEffect(() => {
    if (opened && contract && organizationId) {
      loadFiles();
    }
  }, [opened, contract, organizationId]);

  const loadFiles = async () => {
    setFilesLoading(true);
    try {
      const data = await contractFilesApi.getFiles(organizationId, contract.id);
      setFiles(data.items || []);
    } catch (err) {
      console.error('Failed to load files:', err);
    } finally {
      setFilesLoading(false);
    }
  };

  if (!contract) return null;

  const isInactive = !contract.is_active;
  const statusColor = STATUS_COLORS[contract.status] || 'gray';

  // Форматирование даты
  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    return new Date(dateStr).toLocaleDateString('ru-RU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  // Форматирование даты и времени
  const formatDateTime = (dateStr) => {
    if (!dateStr) return null;
    return new Date(dateStr).toLocaleString('ru-RU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Europe/Moscow',
    });
  };

  // Скачивание файла
  const handleDownload = (fileId) => {
    const url = fileId
      ? `/api/organizations/${organizationId}/contracts/${contract.id}/files/${fileId}/download`
      : `/api/organizations/${organizationId}/contracts/${contract.id}/download`;
    window.open(url, '_blank');
  };

  // Отображаемые версии файлов
  const displayedFiles = showAllVersions ? files : files.slice(0, 1);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Договор"
      size="lg"
      radius="xl"
      styles={{
        content: {
          borderRadius: '16px',
        },
      }}
    >
      <Stack gap="lg">
        {/* Заголовок */}
        <Group>
          <Box
            style={{
              width: 56,
              height: 56,
              borderRadius: '14px',
              backgroundColor: isInactive ? '#ffebee' : '#e3f2fd',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <IconFileText size={28} color={isInactive ? '#c62828' : '#1976d2'} />
          </Box>
          <Box style={{ flex: 1 }}>
            <Group gap="xs">
              <Text size="lg" fw={600}>
                {contract.display_name}
              </Text>
            </Group>
            <Group gap="xs" mt={4}>
              <Badge
                size="sm"
                variant={contract.status === 'active' ? 'filled' : 'light'}
                radius="xl"
                color={statusColor}
              >
                {contract.status_display}
              </Badge>
              {isInactive && (
                <Badge
                  size="sm"
                  variant="filled"
                  radius="xl"
                  color="red"
                  leftSection={<IconX size={10} />}
                >
                  Деактивирован
                </Badge>
              )}
              {contract.edit_count > 0 && (
                <Badge size="xs" variant="light" radius="xl" color="blue">
                  Изменено {contract.edit_count}
                </Badge>
              )}
            </Group>
          </Box>
        </Group>

        <Divider />

        {/* Основная информация */}
        <Stack gap="sm">
          <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
            Информация о договоре
          </Text>

          {contract.contract_number && (
            <InfoField
              icon={IconHash}
              label="Номер договора"
              value={contract.contract_number}
            />
          )}

          <InfoField
            icon={IconCalendar}
            label="Дата подписания"
            value={formatDate(contract.signing_date)}
          />

          <InfoField
            icon={IconCalendar}
            label="Дата начала занятий"
            value={formatDate(contract.start_date)}
          />
        </Stack>

        {/* Предметы */}
        {contract.subjects && contract.subjects.length > 0 && (
          <Stack gap="sm">
            <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
              Предметы занятий
            </Text>
            <Group gap="xs">
              {contract.subjects.map((subject) => (
                <Badge
                  key={subject.id}
                  size="md"
                  variant="light"
                  radius="xl"
                  color="blue"
                >
                  {subject.name}
                </Badge>
              ))}
            </Group>
          </Stack>
        )}

        {/* Комментарий */}
        {contract.comment && (
          <Stack gap="sm">
            <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
              Комментарий
            </Text>
            <Box
              p="sm"
              style={{
                backgroundColor: tokens.colors.neutral[2],
                borderRadius: '8px',
              }}
            >
              <Text size="sm" style={{ whiteSpace: 'pre-line' }}>
                {contract.comment}
              </Text>
            </Box>
          </Stack>
        )}

        {/* Файлы договора */}
        <Stack gap="sm">
          <Group justify="space-between">
            <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
              Файлы договора
            </Text>
            {files.length > 1 && (
              <Button
                variant="subtle"
                size="xs"
                leftSection={<IconHistory size={14} />}
                onClick={() => setShowAllVersions(!showAllVersions)}
              >
                {showAllVersions ? 'Скрыть историю' : `Все версии (${files.length})`}
              </Button>
            )}
          </Group>

          {filesLoading ? (
            <Center py="md">
              <Loader size="sm" />
            </Center>
          ) : files.length > 0 ? (
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Файл</Table.Th>
                  <Table.Th>Версия</Table.Th>
                  <Table.Th>Размер</Table.Th>
                  <Table.Th>Загружен</Table.Th>
                  <Table.Th></Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {displayedFiles.map((file) => (
                  <Table.Tr key={file.id}>
                    <Table.Td>
                      <Group gap="xs">
                        <IconFileText size={16} color={tokens.colors.neutral[6]} />
                        <Text size="sm" truncate style={{ maxWidth: 200 }}>
                          {file.original_filename}
                        </Text>
                        {file.is_current && (
                          <Badge size="xs" variant="filled" color="green" radius="xl">
                            Текущий
                          </Badge>
                        )}
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Badge size="xs" variant="light" color="gray" radius="xl">
                        v{file.version_number}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" c="dimmed">
                        {file.file_size_display}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" c="dimmed">
                        {formatDateTime(file.uploaded_at)}
                      </Text>
                      {file.uploaded_by_name && (
                        <Text size="xs" c="dimmed">
                          {file.uploaded_by_name}
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Tooltip label="Скачать">
                        <ActionIcon
                          variant="subtle"
                          color="blue"
                          onClick={() => handleDownload(file.id)}
                        >
                          <IconDownload size={16} />
                        </ActionIcon>
                      </Tooltip>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          ) : (
            <Text size="sm" c="dimmed" ta="center" py="md">
              Файлы не найдены
            </Text>
          )}
        </Stack>

        {/* Причина деактивации */}
        {isInactive && contract.inactive_reason && (
          <Stack gap="sm">
            <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
              Причина деактивации
            </Text>
            <Box
              p="sm"
              style={{
                backgroundColor: '#fff5f5',
                borderRadius: '8px',
                border: '1px solid #ffcdd2',
              }}
            >
              <Text size="sm" c="red" style={{ whiteSpace: 'pre-line' }}>
                {contract.inactive_reason}
              </Text>
              {contract.inactive_at && (
                <Text size="xs" c="dimmed" mt={4}>
                  {formatDateTime(contract.inactive_at)}
                </Text>
              )}
            </Box>
          </Stack>
        )}

        {/* Метаданные */}
        <Stack gap="xs">
          <Text size="xs" c="dimmed">
            Создан: {formatDateTime(contract.created_at)}
            {contract.created_by_name && ` (${contract.created_by_name})`}
          </Text>
          <Text size="xs" c="dimmed">
            Обновлён: {formatDateTime(contract.updated_at)}
          </Text>
        </Stack>

        <Divider />

        {/* Кнопки действий */}
        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose}>
            Закрыть
          </Button>
          {onUploadFile && (
            <Button
              variant="light"
              leftSection={<IconUpload size={16} />}
              onClick={() => {
                onClose();
                onUploadFile(contract);
              }}
            >
              Загрузить версию
            </Button>
          )}
          {onEdit && (
            <Button
              leftSection={<IconPencil size={16} />}
              onClick={() => {
                onClose();
                onEdit(contract);
              }}
            >
              Редактировать
            </Button>
          )}
        </Group>
      </Stack>
    </Modal>
  );
};

export default ContractViewModal;
